import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	DateRangePicker,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	useIsMobile,
} from '@hotelise/ui';
import type { DateRange } from '@hotelise/ui';

import {
	getPropertyDetailsFilters,
	getPropertyExpenses,
	getPropertyReservations,
	getPropertyStatements,
	setPropertyDetailsFilter,
} from 'state/modules/properties';

import { DateFilterType } from 'interfaces/filters';

interface Props {
	propertyId: string;
}

const PropertyDetailsFilters = ({ propertyId }: Props): JSX.Element => {
	const filter = useSelector(getPropertyDetailsFilters);
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	const [applyTo, setApplyTo] = useState<'checkin' | 'checkout'>(
		filter.dateFromType === DateFilterType.CHECKIN_TIME ? 'checkin' : 'checkout',
	);

	const dateRange: DateRange = {
		from: filter.dateFrom ? new Date(filter.dateFrom) : undefined,
		to: filter.dateTo ? new Date(filter.dateTo) : undefined,
	};

	const refetchData = useCallback(() => {
		dispatch(getPropertyReservations(propertyId));
		dispatch(getPropertyExpenses(propertyId));
		dispatch(getPropertyStatements({ propertyId }));
	}, [dispatch, propertyId]);

	const handleDateChange = useCallback((range: DateRange) => {
		const type = applyTo === 'checkin' ? DateFilterType.CHECKIN_TIME : DateFilterType.CHECKOUT_TIME;
		dispatch(setPropertyDetailsFilter({
			dateFrom: range.from?.toISOString() ?? null,
			dateTo: range.to?.toISOString() ?? null,
			dateFromType: type,
			dateToType: type,
		}));
		if (range.from && range.to) {
			refetchData();
		}
	}, [dispatch, applyTo, refetchData]);

	const handleApplyToChange = useCallback((value: 'checkin' | 'checkout') => {
		setApplyTo(value);
		const type = value === 'checkin' ? DateFilterType.CHECKIN_TIME : DateFilterType.CHECKOUT_TIME;
		dispatch(setPropertyDetailsFilter({
			dateFrom: filter.dateFrom,
			dateTo: filter.dateTo,
			dateFromType: type,
			dateToType: type,
		}));
		refetchData();
	}, [dispatch, filter.dateFrom, filter.dateTo, refetchData]);

	return (
		<div className="flex items-center gap-2">
			<DateRangePicker
				value={dateRange}
				onChange={handleDateChange}
				numberOfMonths={isMobile ? 1 : 2}
			/>
			<Select value={applyTo} onValueChange={(v) => handleApplyToChange(v as 'checkin' | 'checkout')}>
				<SelectTrigger className="w-[120px]">
					<SelectValue placeholder="Checkout" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="checkin">Check-in</SelectItem>
					<SelectItem value="checkout">Check-out</SelectItem>
				</SelectContent>
			</Select>
		</div>
	);
};

export default PropertyDetailsFilters;
